import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "business-hours-section section content-container" }
const _hoisted_2 = {
  key: 1,
  class: "container is-fluid"
}
const _hoisted_3 = { class: "columns" }
const _hoisted_4 = { class: "column" }
const _hoisted_5 = { class: "columns" }
const _hoisted_6 = { class: "column" }
const _hoisted_7 = { class: "no-holidays" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "save-availability" }

import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import TitledSection from '@/shared/components/TitledSection.vue';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import CreateHoliday from '@/features/settings/components/CreateHoliday.vue';
import UpdateHoliday from '@/features/settings/components/UpdateHoliday.vue';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import DataTableColumn from '@/shared/components/dataTable/dataTableColumn';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import { DELETE_HOLIDAY, LOAD_HOLIDAYS } from '@/store/holidays/actionTypes';
import moment from 'moment';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import Toast from '@/shared/services/toast';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import CalendarIcon from 'vue-material-design-icons/CalendarRange.vue';
import BusinessHoursSelection from '@/features/bots/components/businessHoursSelection/BusinessHoursSelection.vue';
import { BusinessHoursViewModel } from '@/features/bots/models';
import { botsApi } from '@/features/bots/services/bots';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'BusinessHours',
  setup(__props) {

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const holidays = computed(() => store.getters.holidays);
const getFlow = computed(() => store.getters.getFlow);

const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);
const loadHolidays = (forceLoad?: boolean) => store.dispatch(LOAD_HOLIDAYS, forceLoad);
const executeDelete = (holidayId: string) => store.dispatch(DELETE_HOLIDAY, holidayId);
const loadFlows = () => store.dispatch(LOAD_FLOWS);

const businessHours = ref<BusinessHoursViewModel[]>([]);
const selectedHoliday = ref<HolidayViewModel>(<HolidayViewModel>{});
const createHolidayModalDisplayed = ref(false);
const updateHolidayModalDisplayed = ref(false);
const showDeleteAttributeModal = ref(false);
const isLoading = ref(true);
const botAppInstallError = ref(false);
const updateInProgress = ref(false);
const invalidOpeningTimes = ref(false);

const isHolidayUsedInDefaultBusinessHours = computed(() =>
  businessHours.value.some((bh) => bh.holidayId === selectedHoliday.value.id),
);
const isHolidayUsedInFlows = computed(
  () => selectedHoliday.value.usedInFlowIds && selectedHoliday.value.usedInFlowIds.length > 0,
);

const usedFlowHtmlList = computed(() => {
  const flowNames = selectedHoliday.value.usedInFlowIds!.map((flowId) => getFlowDisplayName(flowId));
  let flowListHtml = '<ul style="list-style: disc outside">';
  flowListHtml += flowNames.map((flowName) => `<li>${flowName}</li>`).join('');
  flowListHtml += '</ul>';
  return flowListHtml;
});

const holidayDisplayName = computed(() => HtmlFormatter.escapeHtmlCharacters(selectedHoliday.value.name));

const canSelectedHolidayBeDeleted = computed(
  () => !isHolidayUsedInDefaultBusinessHours.value && !isHolidayUsedInFlows.value,
);

const deleteConfirmationMessage = computed(() => {
  if (canSelectedHolidayBeDeleted.value) {
    return `Are you sure you want to delete holiday <b>${holidayDisplayName.value}</b>?`;
  } else {
    let warningText = `The holiday <b>${holidayDisplayName.value}</b> cannot be deleted `;
    if (isHolidayUsedInDefaultBusinessHours.value && isHolidayUsedInFlows.value) {
      warningText += `as it's in use in your default business hours and in the following flows: ${usedFlowHtmlList.value}`;
    } else if (isHolidayUsedInDefaultBusinessHours.value) {
      warningText += `as it's in use in your default business hours.`;
    } else if (isHolidayUsedInFlows.value) {
      warningText += `as it's in use in the following flows: ${usedFlowHtmlList.value}`;
    }
    return warningText;
  }
});

onMounted(async () => {
  try {
    await loadHolidays(true);
    businessHours.value = await loadBusinessHours();
  } catch (error) {
    if (error.status !== 404) {
      handleSeriousError(error.message);
    }
  } finally {
    isLoading.value = false;
  }
});

function showCreateHolidayModal() {
  createHolidayModalDisplayed.value = true;
}

function closeCreateHolidayModal() {
  createHolidayModalDisplayed.value = false;
}

function openEditHoliday(holiday: HolidayViewModel) {
  selectedHoliday.value = holiday;
  updateHolidayModalDisplayed.value = true;
}

function closeUpdateHolidayModal() {
  updateHolidayModalDisplayed.value = false;
}

async function showDeleteModal(holiday: HolidayViewModel) {
  selectedHoliday.value = holiday;
  if (canSelectedHolidayBeDeleted.value) {
    showDeleteAttributeModal.value = true;
  } else {
    if (isHolidayUsedInFlows.value) {
      await loadFlows();
    }
    showDeleteAttributeModal.value = true;
  }
}

async function deleteHoliday() {
  showDeleteAttributeModal.value = false;
  if (canSelectedHolidayBeDeleted.value) {
    try {
      await executeDelete(selectedHoliday.value.id!);
      Toast.showSuccess(`Successfully deleted holiday: ${selectedHoliday.value.name}`);
    } catch (err) {
      Toast.showError(err.message);
    }
  }
  selectedHoliday.value = {} as HolidayViewModel;
}

function getColumns(): DataTableColumn[] {
  return [
    {
      display: 'Name',
      name: 'name',
      clickable: true,
    },
    {
      display: 'When',
      name: 'dates',
      formatter: formatWhen,
    },
  ];
}

function loadBusinessHours() {
  return botsApi.getBusinessHours(currentWorkspace.value.id);
}

function openingTimesError($event: any) {
  invalidOpeningTimes.value = $event;
}

function save() {
  if (updateInProgress.value || isLoading.value || botAppInstallError.value) {
    return;
  }
  updateInProgress.value = true;

  const mappedHours = businessHours.value.map((val) => {
    if (val.closed) {
      val.startTime = undefined;
      val.endTime = undefined;
    }
    return val;
  });

  botsApi
    .updateBusinessHours(currentWorkspace.value.id, mappedHours)
    .then(() => {
      Toast.showSuccess('Successfully updated Bot');
    })
    .catch((err) => {
      Toast.showError(err.message);
    })
    .finally(() => {
      updateInProgress.value = false;
    });
}

function formatWhen(dates: Date[]): string {
  if (dates) {
    const datesToDisplay = dates.slice(0, 3);
    const readableDates = datesToDisplay.map((date) => moment(date).format('MMMM Do YYYY')).join(', ');
    return dates.length > 3 ? `${readableDates}...` : readableDates;
  }
  return '';
}

function getFlowDisplayName(flowId: string): string {
  const flow = getFlow.value(flowId);
  return flow ? HtmlFormatter.escapeHtmlCharacters(flow.name) : flowId;
}

return (_ctx: any,_cache: any) => {
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_data_table = _resolveComponent("n-data-table")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(_component_n_loading_spinner, { key: 0 }))
      : _createCommentVNode("", true),
    (!isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(TitledSection, { class: "container is-fluid island" }, {
                title: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                  _createTextVNode("Default Business Hours", -1)
                ]))]),
                subtitle: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode(" Define your standard operating hours for your workspace to manage customer expectations and internal workflows ", -1)
                ]))]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createVNode(BusinessHoursSelection, {
                        class: "business-hour-selection",
                        modelValue: businessHours.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((businessHours).value = $event)),
                        onError: _cache[1] || (_cache[1] = ($event: any) => (openingTimesError($event))),
                        enforceOnlineDayOfWeek: true
                      }, null, 8, ["modelValue"])
                    ])
                  ])
                ]),
                _: 1
              }),
              _createVNode(TitledSection, { class: "container is-fluid holidays-section island" }, {
                title: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                  _createTextVNode("Holidays", -1)
                ]))]),
                subtitle: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                  _createTextVNode("Specify dates where usual business hours do not apply", -1)
                ]))]),
                actions: _withCtx(() => [
                  _createVNode(_component_n_button, {
                    variant: "ghost",
                    icon: "add",
                    onClick: showCreateHolidayModal
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode("Add holiday", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createElementVNode("div", null, [
                    _createVNode(_component_n_data_table, {
                      items: holidays.value,
                      columns: getColumns(),
                      onCellClicked: openEditHoliday
                    }, {
                      nodata: _withCtx(() => [
                        _createElementVNode("div", _hoisted_7, [
                          _createVNode(CalendarIcon, { class: "icon-2x" }),
                          _cache[8] || (_cache[8] = _createElementVNode("div", { class: "no-holidays-message" }, [
                            _createElementVNode("h1", null, "No holidays have been added")
                          ], -1))
                        ])
                      ]),
                      actions: _withCtx(({ row }) => [
                        _createVNode(DropDownMenu, {
                          goLeft: true,
                          dropDownIcon: "dots"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("a", {
                              onClick: ($event: any) => (showDeleteModal(row)),
                              class: "dropdown-item"
                            }, [
                              _createVNode(DeleteIcon),
                              _cache[9] || (_cache[9] = _createElementVNode("span", { class: "action-label" }, "Delete", -1))
                            ], 8, _hoisted_8)
                          ]),
                          _: 2
                        }, 1024)
                      ]),
                      _: 1
                    }, 8, ["items", "columns"])
                  ]),
                  (createHolidayModalDisplayed.value)
                    ? (_openBlock(), _createBlock(CreateHoliday, {
                        key: 0,
                        onCancel: closeCreateHolidayModal,
                        onCreate: closeCreateHolidayModal
                      }))
                    : _createCommentVNode("", true),
                  (updateHolidayModalDisplayed.value)
                    ? (_openBlock(), _createBlock(UpdateHoliday, {
                        key: 1,
                        holiday: selectedHoliday.value,
                        onCancel: closeUpdateHolidayModal,
                        onUpdate: closeUpdateHolidayModal
                      }, null, 8, ["holiday"]))
                    : _createCommentVNode("", true),
                  (showDeleteAttributeModal.value)
                    ? (_openBlock(), _createBlock(ConfirmationModal, {
                        key: 2,
                        confirmationTitle: "Delete Holiday",
                        confirmationMessage: deleteConfirmationMessage.value,
                        "confirm-button-label": canSelectedHolidayBeDeleted.value ? 'Yes' : 'Ok',
                        "show-cancel-button": canSelectedHolidayBeDeleted.value,
                        onConfirm: deleteHoliday,
                        onCancel: _cache[2] || (_cache[2] = ($event: any) => (showDeleteAttributeModal.value = false))
                      }, null, 8, ["confirmationMessage", "confirm-button-label", "show-cancel-button"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_9, [
                _createVNode(_component_n_button, {
                  variant: "primary",
                  class: "save-button",
                  loading: updateInProgress.value,
                  disabled: invalidOpeningTimes.value,
                  onClick: save
                }, {
                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                    _createTextVNode("Save ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading", "disabled"])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})